<template>
    <form @submit.prevent="onSubmit">
        <slot
            :errors="errors"
            :are-errors-visible="areErrorsVisible"
            :is-valid="isValid"
        />
    </form>
</template>
<script setup>
import { nextTick, computed, reactive, watch, provide, isReactive } from 'vue';
import { mapValues } from 'lodash-es';
import { useI18n } from '#imports';
import { formInjectionKey, validators } from '~dsc/utils/form';
const i18n = useI18n();

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    rules: {
        type: Object,
        required: true,
    },
    skipValidationOnChange: {
        type: Boolean,
        default: false,
    },
    doNotForceErrorsOnChange: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'update',
    'update:modelValue',
    'areErrorsVisible',
    'isValid',
    'error',
    'submit',
]);

const fieldState = reactive(mapValues(props.rules, r => ({
    errors: null,
    hasBeenValidOrShownError: !!r[0]?.opts?.hasBeenValidOrShownError,
})));
const errors = computed(() => mapValues(fieldState, f => f.errors));

const areErrorsVisible = computed(() => Object.values(fieldState).some(f => f.errors !== null));
watch(areErrorsVisible, value => emit('areErrorsVisible', value), { immediate: true });

const isValid = computed(() => Object.values(fieldState).every(f => f.errors === null && f.hasBeenValidOrShownError));
watch(isValid, value => emit('isValid', value), { immediate: true });

let commitingValue = false;

watch(props.modelValue, () => {
    if (!commitingValue && !props.skipValidationOnChange) validateForm({ forceErrors: !props.doNotForceErrorsOnChange });
});

const onSubmit = () => {
    validateForm();
    if (isValid.value) emit('submit');
    else emit('error', errors.value);
};

async function commitValue(fieldName, value) {
    commitingValue = true;
    try {
        if (isReactive(props.modelValue)) {
            // eslint-disable-next-line vue/no-mutating-props
            props.modelValue[fieldName] = value;
            emit('update');
        } else {
            emit('update:modelValue', {
                ...props.modelValue,
                [fieldName]: value,
            });
        }
        await nextTick();
        validateField(fieldName);
    } finally {
        commitingValue = false;
    }
}

function validateField(fieldName, { forceErrors = false } = {}) {
    const value = props.modelValue[fieldName];
    const fieldErrors = [];

    for (const rule of props.rules[fieldName]) {
        if (rule.opts) continue;

        const validatorName = rule.type ?? rule;
        const validator = validators[validatorName];

        if (typeof validator !== 'function') throw new Error(`Invalid validator rule ${validatorName}`);

        const error = validator(value, rule, { fieldName, fieldState, state: props.modelValue, forceErrors });

        if (error) {
            const { info } = console;
            if (import.meta.dev) info('[DSForm] Errored field:', fieldName);

            fieldErrors.push(i18n.t(error, {
                value,
                ruleValue: rule.value,
            }, rule.plural));
        }
    }

    const hasBeenValid = fieldState[fieldName].hasBeenValidOrShownError || !fieldErrors.length;
    fieldState[fieldName].hasBeenValidOrShownError = hasBeenValid;
    if (!fieldErrors.length) {
        fieldState[fieldName].errors = null;
    } else if (hasBeenValid || forceErrors) {
        fieldState[fieldName].errors = fieldErrors;
        fieldState[fieldName].hasBeenValidOrShownError = true;
    } else {
        fieldState[fieldName].errors = null;
    }
}

function validateForm(options = {}, fields = []) {
    for (const fieldName of Object.keys(props.rules)) {
        if (!fields.length || fields.includes(fieldName)) {
            validateField(fieldName, {
                forceErrors: true,
                ...options,
            });
        }
    }
}

const form = computed(() => ({
    state: props.modelValue,
    rules: props.rules,
    fieldState,
    commitValue,
    validateField,
    submit: onSubmit,
}));
provide(formInjectionKey, form);

defineExpose({
    validateForm,
});
</script>
